import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { ChevronDown, AlertTriangle } from "lucide-react";
import { useState } from "react";
import { useGetPlanCoverageGaps, getGetPlanCoverageGapsQueryKey } from "@workspace/api-client-react";
import { Link } from "wouter";

/**
 * VAL-05 plan-coverage gaps panel.
 * - On Charges Inbox header: collapsed by default; expandable list (UI-SPEC §Pattern 5).
 * - On Member Create form: pass `planId` to scope to a single plan; renders inline alert if gaps exist.
 */
interface Props {
  planId?: number;             // when set, filter to this single plan
  variant?: "panel" | "inline"; // panel = Charges Inbox header; inline = Members form
}

export function PlanCoverageAlert({ planId, variant = "panel" }: Props) {
  const [open, setOpen] = useState(false);
  const { data: gaps } = useGetPlanCoverageGaps(undefined, {
    query: { queryKey: getGetPlanCoverageGapsQueryKey() },
  });
  const filtered = (gaps ?? []).filter((g) => planId == null || g.planId === planId);
  if (filtered.length === 0) return null;

  if (variant === "inline") {
    return (
      <Alert variant="default" className="border-amber-200 bg-amber-50 mt-2">
        <AlertTriangle className="h-4 w-4 text-amber-600" />
        <AlertTitle className="text-amber-700">Plan coverage warning</AlertTitle>
        <AlertDescription className="text-amber-800">
          {filtered[0]!.planName} has missing entitlements for {filtered[0]!.missingItems.length} required items.
          Adding members on this plan may produce billing inaccuracies.{" "}
          <Link href="/inventory/operation" className="underline">View entitlements</Link>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="mb-4">
      <Alert variant="default" className="border-amber-200 bg-amber-50">
        <AlertTriangle className="h-4 w-4 text-amber-600" />
        <div className="flex items-center justify-between w-full">
          <div>
            <AlertTitle className="text-amber-700">{filtered.length} plan(s) with coverage gaps</AlertTitle>
            <AlertDescription className="text-amber-800">VAL-05 audit — plans missing entitlement rows for required items</AlertDescription>
          </div>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" aria-label="Toggle gap list">
              <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
            </Button>
          </CollapsibleTrigger>
        </div>
        <CollapsibleContent className="mt-3 space-y-2">
          {filtered.map((g) => (
            <div key={g.planId} className="border rounded p-2 bg-white">
              <div className="font-semibold text-sm">{g.planName}</div>
              <div className="flex flex-wrap gap-1 mt-1">
                {g.missingItems.map((it) => (
                  <Badge key={it.itemId} className="bg-red-100 text-red-700 border-red-200 text-xs" variant="outline">
                    {it.itemName} ({it.chargingModel})
                  </Badge>
                ))}
              </div>
            </div>
          ))}
        </CollapsibleContent>
      </Alert>
    </Collapsible>
  );
}
